import * as React from "react"
import { useLocation, useNavigate } from "react-router-dom"

import debounce from "lodash.debounce"

import passwordStrength from "@podcastsoundboard/ps-lib/api/auth/passwordStrength"

import PageContainer from "@podcastsoundboard/ps-lib/components/PageContainer"
import completeResetPasswordRequest from "../../api/resetPasswordRequests/update"
import AppBar from "../../components/AppBar"
import Copyright from "../../components/Copyright"
import { useAppDispatch, useAppSelector } from "../../redux"
import { addSnackbar } from "../../redux/snackbars"
import {
  Alert,
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  Link,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
  AlertColor,
} from "@podcastsoundboard/ps-lib/ui"
import LockOutlinedIcon from "@podcastsoundboard/ps-lib/ui/icons/LockOutlined"

export default function CompletePasswordReset() {
  const dispatch = useAppDispatch()
  const { search } = useLocation()
  const navigate = useNavigate()

  const [submitting, setSubmitting] = React.useState(false)
  const [passwordValidationErrorMessage, setPasswordValidationErrorMessage] =
    React.useState("")

  const [password, setPassword] = React.useState("")

  const [errorMessage, setErrorMessage] = React.useState("")
  const [messageSeverity, setMessageSeverity] =
    React.useState<AlertColor>("error")

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    setSubmitting(true)
    const params = new URLSearchParams(search)
    const token = params.get("token")
    const email = params.get("email")
    if (!email) return setErrorMessage("No email provided")
    if (!token) return setErrorMessage("No token provided")

    completeResetPasswordRequest(email, password, token)
      .then(() => {
        setSubmitting(false)

        dispatch(
          addSnackbar({
            id: "passwordResetComplete",
            text: "Password reset complete.",
          }),
        )

        navigate("/app/signin")
      })
      .catch((err) => {
        console.error(err)
        setSubmitting(false)
        setMessageSeverity("error")
        setErrorMessage(
          (err.response?.data as { error?: string } | undefined)?.error ||
            err.message,
        )
      })
  }

  React.useEffect(() => {
    const debounced = debounce(() => {
      if (!password) return setPasswordValidationErrorMessage("")

      passwordStrength(password)
        .then(({ errorMessage }) => {
          setPasswordValidationErrorMessage(errorMessage)
        })
        .catch((err) => {
          console.error("Error checking password strength", err)
          dispatch(
            addSnackbar({
              id: "pwdcheckerr",
              text: "Error checking password strength.",
            }),
          )
        })
    }, 200)

    debounced()

    return debounced.cancel
  }, [dispatch, password, setPasswordValidationErrorMessage])

  const submitDisabled = !(password && !passwordValidationErrorMessage)
  const darkModeEnabled = useAppSelector((state) => state.darkModeEnabled)

  return (
    <PageContainer darkModeEnabled={darkModeEnabled} className="min-h-screen">
      <AppBar title="Podcast Soundboard" />

      <Toolbar />

      {errorMessage && (
        <Stack className="w-full" spacing={2}>
          <Alert className="rounded-none" severity={messageSeverity}>
            {errorMessage}
          </Alert>
        </Stack>
      )}

      <Container component="main" maxWidth="xs">
        <Paper elevation={4} className="p-8 mt-8 flex flex-col items-center">
          <Avatar className="m-2 bg-primary">
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5" color="text.primary">
            Complete Password Reset
          </Typography>

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            className="mt-6"
            width="100%"
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="password"
                  label="New password"
                  type="password"
                  name="password"
                  value={password || ""}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </Grid>
            </Grid>

            {passwordValidationErrorMessage && (
              <Typography className="mt-4" color="error">
                {passwordValidationErrorMessage}
              </Typography>
            )}

            <Button
              disabled={submitting || submitDisabled}
              type="submit"
              fullWidth
              variant="contained"
              className="mt-6 mb-4"
            >
              {submitting ? "Submitting..." : "Submit"}
            </Button>
            <Grid container justifyContent="center">
              <Grid item style={{ paddingRight: "1rem" }}>
                <Link href="/app/signin" variant="body2">
                  Sign In
                </Link>
              </Grid>
              <Grid item>
                <Link href="/app/signup" variant="body2">
                  Sign Up
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <Copyright className="mt-10" />
      </Container>
    </PageContainer>
  )
}
